import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChatList } from '@/components/chat/ChatList';
import { ChatWindow } from '@/components/chat/ChatWindow';
import { useAuth } from '@/contexts/AuthContext';
import DashboardSidebar from '@/components/dashboard/DashboardSidebar';
import { Button } from '@/components/ui/button';
import { Menu, ArrowLeft, Edit } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';

const ChatPage: React.FC = () => {
  const { threadId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showChatList, setShowChatList] = useState(!threadId);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!user) {
      navigate('/login');
    }
  }, [user, navigate]);

  useEffect(() => {
    // On mobile, when a thread is selected, hide the chat list
    if (threadId) {
      setShowChatList(false);
    }
  }, [threadId]);

  if (!user) {
    return null;
  }

  if (user.role === 'admin') {
    return (
      <div className="flex h-screen">
        <DashboardSidebar role="admin" />
        <div className="flex-1 p-8">
          <h1 className="text-2xl font-bold mb-4">Chat Access</h1>
          <p>Chat functionality is only available for students and instructors.</p>
        </div>
      </div>
    );
  }

  const handleBackToList = () => {
    setShowChatList(true);
    navigate('/chat');
  };

  const handleEditCourse = (course: any) => {
    // Implement the logic to handle editing a course
    console.log('Editing course:', course);
  };

  return (
    <div className="h-screen flex flex-col md:flex-row overflow-hidden bg-background text-foreground">
      {/* Mobile Menu */}
      <div className="md:hidden">
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="md:hidden fixed top-4 left-4 z-[100] bg-white shadow-md hover:bg-gray-100"
            >
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-[280px] sm:w-[320px]">
            <DashboardSidebar 
              role={user.role as 'student' | 'instructor'} 
              isMobile={true} 
              onClose={() => setIsMobileMenuOpen(false)} 
            />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <div className="hidden md:block">
        <DashboardSidebar role={user.role as 'student' | 'instructor'} />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col md:flex-row h-full">
        {/* Chat List */}
        <div
          className={`${
            showChatList ? 'flex' : 'hidden'
          } md:flex w-full md:w-80 border-r border-gray-200 flex-col`}
        >
            <ChatList userId={user.id} role={user.role} />
          </div>

        {/* Chat Window */}
        <div
          className={`${
            !showChatList ? 'flex' : 'hidden'
          } md:flex flex-1 flex-col relative`}
        >
            {threadId ? (
            <>
              {/* Back to Conversations Button - Mobile Only */}
              <div className="md:hidden pt-16 px-4 border-b">
                <Button
                  variant="ghost"
                  className="flex items-center gap-2 mb-2"
                  onClick={handleBackToList}
                >
                  <ArrowLeft className="h-5 w-5" />
                  <span>Back to Conversations</span>
                </Button>
              </div>
              <ChatWindow threadId={threadId} />
            </>
            ) : (
            <div className="hidden md:flex items-center justify-center h-full">
                <p className="text-gray-500">Select a conversation to start chatting</p>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default ChatPage; 